import { useEffect } from "react";
import { HeadContent, Outlet, Scripts, createRootRoute } from "@tanstack/react-router";
import { StickyNav } from "#/components/layout/StickyNav";
import { Footer } from "#/components/layout/Footer";
import { MobileActionBar } from "#/components/shared/MobileActionBar";
import { getLocalBusinessSchema } from "#/lib/seo";
import { OG_IMAGE } from "#/lib/images";

import appCss from "../styles.css?url";

const TITLE = "Blue Rock Concrete | Concrete Contractor in Wilmington, DE";
const DESCRIPTION =
  "Driveways, patios, stamped concrete, repair & epoxy floors in Wilmington, DE. Licensed, bonded & insured. Free same-week estimates. Built for Delaware winters.";

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      // viewport-fit=cover is required for env(safe-area-inset-*) to resolve to
      // anything but 0, which the fixed mobile action bar depends on to clear
      // the iPhone home indicator.
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1, viewport-fit=cover",
      },
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Blue Rock Concrete" },
      { property: "og:image", content: OG_IMAGE },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: OG_IMAGE },
      { name: "theme-color", content: "#000000" },
    ],
    links: [
      { rel: "preconnect", href: "https://images.unsplash.com" },
      { rel: "stylesheet", href: appCss },
      // Tab and app icons. The SVG is what modern browsers use; the 96px PNG
      // is the raster Google Search prefers when it picks a favicon for a
      // result; apple-touch-icon is the iOS home screen and Safari tab.
      { rel: "icon", href: "/favicon.ico", sizes: "48x48" },
      { rel: "icon", href: "/favicon-32.png", type: "image/png", sizes: "32x32" },
      { rel: "icon", href: "/favicon-96.png", type: "image/png", sizes: "96x96" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png", sizes: "180x180" },
      { rel: "manifest", href: "/site.webmanifest" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(getLocalBusinessSchema()),
      },
    ],
  }),
  component: RootComponent,
  shellComponent: RootDocument,
});

function RootDocument({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body className="font-body antialiased">
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  // On a fresh load with a #hash, the router's scroll restoration and the CSS
  // smooth-scroll cancel each other's animated jumps and the page ends up at
  // the top. Re-apply the hash scroll once, after hydration, as an instant
  // jump that nothing can cancel mid-animation.
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (!hash) return;
    const el = document.getElementById(hash);
    if (!el) return;
    const t = setTimeout(() => el.scrollIntoView({ behavior: "instant" }), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <>
      <StickyNav />
      {/* has-action-bar reserves the bottom space the fixed MobileActionBar
          occupies, so the footer is never partly underneath it. Applied here
          once rather than per route. */}
      <div className="has-action-bar">
        <Outlet />
        <Footer />
      </div>
      <MobileActionBar />
    </>
  );
}
